import { useState, useRef, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Bell, X, Check, CheckCheck, AlertTriangle, Info, CheckCircle, XCircle } from 'lucide-react'
import { useNotifications, NOTIFICATION_TYPES } from '../contexts/NotificationContext'

/**
 * NotificationCenter - Bell icon with dropdown showing all notifications
 */
export default function NotificationCenter() {
    const [isOpen, setIsOpen] = useState(false)
    const dropdownRef = useRef(null)

    const {
        notifications,
        unreadCount,
        markAsRead,
        markAllAsRead,
        clearNotification
    } = useNotifications()

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false)
            }
        }

        document.addEventListener('mousedown', handleClickOutside)
        return () => document.removeEventListener('mousedown', handleClickOutside)
    }, [])

    // Get icon for notification type
    const getIcon = (type) => {
        switch (type) {
            case NOTIFICATION_TYPES.SUCCESS:
                return <CheckCircle className="w-4 h-4 text-green-400" />
            case NOTIFICATION_TYPES.WARNING:
                return <AlertTriangle className="w-4 h-4 text-yellow-400" />
            case NOTIFICATION_TYPES.ERROR:
                return <XCircle className="w-4 h-4 text-red-400" />
            default:
                return <Info className="w-4 h-4 text-primary-400" />
        }
    }

    // Format relative time
    const formatTime = (date) => {
        const now = new Date()
        const diff = now - date
        const minutes = Math.floor(diff / 60000)
        const hours = Math.floor(diff / 3600000)
        const days = Math.floor(diff / 86400000)

        if (minutes < 1) return 'Just now'
        if (minutes < 60) return `${minutes}m ago`
        if (hours < 24) return `${hours}h ago`
        return `${days}d ago`
    }

    return (
        <div className="relative" ref={dropdownRef}>
            {/* Bell Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="relative p-2 rounded-lg text-white/60 hover:text-white hover:bg-white/10 transition-colors"
                aria-label="Notifications"
            >
                <Bell className="w-5 h-5" />
                {unreadCount > 0 && (
                    <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-xs font-bold">
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </span>
                )}
            </button>

            {/* Dropdown Panel */}
            {isOpen && (
                <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-slate-900 border border-white/10 rounded-xl shadow-xl shadow-black/20 z-50 overflow-hidden">
                    {/* Header */}
                    <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                        <h3 className="font-semibold text-white">Notifications</h3>
                        {notifications.length > 0 && (
                            <button
                                onClick={markAllAsRead}
                                className="text-xs text-primary-400 hover:text-primary-300 flex items-center gap-1"
                            >
                                <CheckCheck className="w-3 h-3" />
                                Mark all read
                            </button>
                        )}
                    </div>

                    {/* Notifications List */}
                    <div className="max-h-80 overflow-y-auto">
                        {notifications.length === 0 ? (
                            <div className="py-8 text-center text-white/40">
                                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                <p className="text-sm">No notifications yet</p>
                            </div>
                        ) : (
                            notifications.map((notification) => (
                                <div
                                    key={notification.id}
                                    className={`px-4 py-3 border-b border-white/5 hover:bg-white/5 transition-colors ${!notification.read ? 'bg-white/[0.02]' : ''
                                        }`}
                                >
                                    <div className="flex items-start gap-3">
                                        <div className="mt-0.5">
                                            {getIcon(notification.type)}
                                        </div>
                                        <div className="flex-grow min-w-0">
                                            <div className="flex items-start justify-between gap-2">
                                                <p className={`text-sm font-medium ${notification.read ? 'text-white/70' : 'text-white'
                                                    }`}>
                                                    {notification.title}
                                                </p>
                                                <span className="text-xs text-white/40 whitespace-nowrap">
                                                    {formatTime(notification.timestamp)}
                                                </span>
                                            </div>
                                            <p className="text-sm text-white/50 mt-0.5">
                                                {notification.message}
                                            </p>
                                            {notification.action && (
                                                <Link
                                                    to={notification.action.href}
                                                    onClick={() => {
                                                        markAsRead(notification.id)
                                                        setIsOpen(false)
                                                    }}
                                                    className="inline-block mt-2 text-xs font-medium text-primary-400 hover:text-primary-300"
                                                >
                                                    {notification.action.label} →
                                                </Link>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-1">
                                            {!notification.read && (
                                                <button
                                                    onClick={() => markAsRead(notification.id)}
                                                    className="p-1 text-white/30 hover:text-white/60"
                                                    title="Mark as read"
                                                >
                                                    <Check className="w-3 h-3" />
                                                </button>
                                            )}
                                            <button
                                                onClick={() => clearNotification(notification.id)}
                                                className="p-1 text-white/30 hover:text-red-400"
                                                title="Dismiss"
                                            >
                                                <X className="w-3 h-3" />
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* Footer */}
                    {notifications.length > 0 && (
                        <div className="px-4 py-2 border-t border-white/10 text-center">
                            <Link
                                to="/settings"
                                onClick={() => setIsOpen(false)}
                                className="text-xs text-white/40 hover:text-white/60"
                            >
                                Manage notification preferences
                            </Link>
                        </div>
                    )}
                </div>
            )}
        </div>
    )
}
